import { useEffect } from 'react';
import { useRouter } from 'next/router';
import Script from 'next/script';

import { useConsentManager } from './consent-manager';

/** Intercom workspace / app ID */
const INTERCOM_APP_ID = 'bxjk2bt8';

declare global {
  interface Window {
    /** Intercom Messenger API */
    Intercom?: ((...args: unknown[]) => void) & { q?: unknown[] };
    /** Intercom boot settings for anonymous visitors */
    intercomSettings?: {
      api_base: string;
      app_id: string;
    };
  }
}

/**
 * Load the Intercom Messenger for anonymous website visitors after airgap.js is initialized.
 * @see https://developers.intercom.com/installing-intercom/web/installation#for-website-visitors-without-logins
 */
export const IntercomScript: React.FC = () => {
  const { airgap } = useConsentManager();
  const router = useRouter();

  // Keep Intercom in sync with client-side navigations
  useEffect(() => {
    const handleRouteChange = () => {
      if (typeof window.Intercom === 'function') {
        window.Intercom('update');
      }
    };
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  return (
    <>
      {airgap && (
        <Script
          id="intercom"
          strategy="lazyOnload"
          dangerouslySetInnerHTML={{
            __html: `
              var APP_ID = "${INTERCOM_APP_ID}";

              window.intercomSettings = {
                api_base: "https://api-iam.intercom.io",
                app_id: APP_ID
              };

              (function(){var w=window;var ic=w.Intercom;if(typeof ic==="function"){ic('update',w.intercomSettings);}else{var d=document;var i=function(){i.c(arguments);};i.q=[];i.c=function(args){i.q.push(args);};w.Intercom=i;var l=function(){var s=d.createElement('script');s.type='text/javascript';s.async=true;s.src='https://widget.intercom.io/widget/' + APP_ID;var x=d.getElementsByTagName('script')[0];x.parentNode.insertBefore(s,x);};if(document.readyState==='complete'){l();}else if(w.attachEvent){w.attachEvent('onload',l);}else{w.addEventListener('load',l,false);}}})();
            `,
          }}
        />
      )}
    </>
  );
};
